import { useReducer, useEffect } from 'react';

const { getAuthenticatedHttpClient } = require('@edx/frontend-platform/auth');
const { getConfig } = require('@edx/frontend-platform');

const initialState = {
  isComplete: false,
  isPassing: false,
  isGraded: false,
  gradePercent: 0,
  completePercent: 0,
};

interface State {
  isComplete: boolean;
  isPassing: boolean;
  isGraded: boolean;
  gradePercent: number;
  completePercent: number;
}

interface Action {
  type: string;
  state?: State;
}

interface Data {
  completion_summary: {
    complete_count: number,
    incomplete_count: number,
    locked_count: number,
  },
  grading_policy: {
    assignment_policies: Object[]
  },
  course_grade: {
    letter_grade: string | null,
    percent: number,
    is_passing: boolean,
  },
}

interface OnSuccess {
  (data?: Object): void;
}

interface OnFailure {
  (error?: Error): void;
}

/**
 * Function that specifies how the state gets updated.
 * @param  {State} state   Current state.
 * @param  {Action} action Describes kind of action to be executed.
 * @return {Object}        Updated state based on the action type.
 */
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_PROGRESS_STATUS':
      return action.state ? action.state : state;
    default:
      return initialState;
  }
}

/**
 * This makes a GET request and allow to execute a function in case on success or on failure.
 * @param  {String}       courseID   Course identifier as string.
 * @param  {OnSuccess}    onSuccess  Function that will be executed on 20X responses.
 * @param  {OnFailure}    onFailure  Function that will be executed on responses different from 20X responses.
 */
function getProgressData(courseId: string, onSuccess: OnSuccess, onFailure: OnFailure) {
  const url = `${getConfig().LMS_BASE_URL}/api/course_home/progress/${courseId}`;
  const apiConfig = { headers: { accept: 'application/json' } };

  getAuthenticatedHttpClient().get(url, apiConfig).then(({ data }: { data: Data }) => {
    onSuccess(data);
  }, (error: Error) => {
    onFailure(error);
  });
}

/**
 * Custom hook that retrieves progress status for a given course.
 *
 * @param   {string}  courseId  Course identifier as String.
 * @returns {State}   State     Returns progress variables.
 */
function useProgressStatus(courseId: string): State {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const getOnSuccess = (data: Data) => {
      // On success the state will be updated based on the API response,
      // some calculation are performed in order to get the real value.
      const summary = data.completion_summary;
      const courseGrade = data.course_grade;
      const total = summary.complete_count + summary.incomplete_count + summary.locked_count;

      const isComplete = summary.incomplete_count === 0;
      const completePercent = total ? summary.complete_count / total : 0;

      const gradePercent = courseGrade.percent;
      const isPassing = courseGrade.is_passing;

      const isGraded = data.grading_policy.assignment_policies.length > 0;

      dispatch({
        type: 'SET_PROGRESS_STATUS',
        state: {
          isComplete, isPassing, isGraded, completePercent, gradePercent,
        },
      });
    };
    const getOnFailure = (error: Error) => {
      // On failure the error will be logged on the browser console and the state will be set to the default value.
      // eslint-disable-next-line no-console
      console.error('Unable to retrive progress data with error:', error.message);
      dispatch({ type: 'DEFAULT' });
    };
    getProgressData(courseId, getOnSuccess, getOnFailure);
  }, [courseId]);

  return state;
}

export default useProgressStatus;
